import { useStores } from '@monorepo/controlled/src/hooks/use-stores';
import styles from './create-domain-factors.module.scss';
import { useEffect, useState } from 'react';
import { PrimaryButton } from '@monorepo/base/src/components/buttons/buttons';
import { DomainFactorsForm } from '../../../modules/components/forms/domain-factors/domain-factors.form';
import { observer } from 'mobx-react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { DispatchLogsTypes, useLogs } from '@monorepo/controlled/src/hooks/use-logs';
import { Page, Pagebar, PagebarTitle } from '@monorepo/base/src/components/page/page';
import { SiderSlider } from '@monorepo/base/src/components/sider-slider/sider-slider';
import { BetweenPagesStateProps } from '@monorepo/tools/src/lib/interfaces/global';
import routes from '../../../modules/routes/index.routes';
import { ErrorMsg } from '../../../modules/components/error-msg/error-msg';
import { BottomBar, BottomBarActions } from '@monorepo/base/src/components/bottom-bar/bottom-bar';
import { IAdminxStore } from '../../../modules/stores';
import { DataAttribute } from '@monorepo/tools/src/lib/models/data-attr.model';
import { useNavigateParams } from '@monorepo/tools/src/lib/hooks/tools/use-navigate-params';

export const CreateDomainFactors = observer(() => {
	const { domainFactorsStore, toastsStore } = useStores<IAdminxStore>();
	const [isFormClicked, setFormClicked] = useState<boolean>(false);
	const domainFactorCrudStore = domainFactorsStore.getCrud();

	const { dispatchLog } = useLogs();
	const navigate = useNavigateParams();
	const location = useLocation();
	const [searchParams] = useSearchParams();

	// reset and prefill must happen here, in the parent, because child effects run first
	useEffect(() => {
		domainFactorCrudStore.reset();
		const campaignIdParam = searchParams.get('campaign_id');
		const domainParam = searchParams.get('domain');
		if (campaignIdParam) {
			domainFactorCrudStore.getData().setCampaignId(campaignIdParam);
		}
		if (domainParam) {
			domainFactorCrudStore.getData().setDomain(domainParam);
		}
	}, [location]);

	const domain = domainFactorCrudStore.getData().getDomain();
	const formStore = domainFactorCrudStore.getFormStore();
	const isValidForm = formStore.getIsValid();
	const isLoading = domainFactorCrudStore.getIsLoading();
	const isSuccess = domainFactorCrudStore.getIsSuccess();
	const error = domainFactorCrudStore.getHttpError();

	useEffect(() => {
		if (!isValidForm && isFormClicked) {
			dispatchLog({
				msg: 'Please complete all mandatory fields',
				type: DispatchLogsTypes.Error,
			});
		}
	}, [isFormClicked]);

	useEffect(() => {
		if (isSuccess) {
			navigate(`${routes.domainFactors.index()}`, {
				state: { isBlockRequest: false } as BetweenPagesStateProps,
			});
			toastsStore.addToastOnNav({ msg: `Domain factor ${domain} was successfully added`, type: DispatchLogsTypes.Success });
			domainFactorCrudStore.setIsSuccess(false);
		}
	}, [isSuccess]);

	const onCreateDomainFactor = () => {
		setFormClicked(true);
		domainFactorsStore.getCrud().create();
	};

	return (
		<SiderSlider
			onClose={() => navigate(routes.domainFactors.index(), { state: { isBlockRequest: true } as BetweenPagesStateProps })}
			isOpen={true}>
			<Page className={isLoading ? styles.loadingPage : ''}>
				<Pagebar>
					<PagebarTitle>
						<div className={styles.title}>Create Domain Factor</div>
					</PagebarTitle>
				</Pagebar>
				<DomainFactorsForm />
			</Page>
			<BottomBar>
				{error ? <ErrorMsg error={error} /> : null}
				<BottomBarActions>
					<PrimaryButton
						className={styles.createBtn}
						onClick={() => onCreateDomainFactor()}
						debugProps={{ dataAttrs: [new DataAttribute('id', 'create_domain_factor_button')] }}
						isRotateIcon={isLoading}
						icon={isLoading ? 'loading-01' : 'send-03'}>
						Create
					</PrimaryButton>
				</BottomBarActions>
			</BottomBar>
		</SiderSlider>
	);
});
